import { BookingGateWayModeIdEnum, dateFormatLongMonth } from '@alluradirect/react';
import { Hidden, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import { AppRadioGroup, AppRadioGroupProps } from 'components/core/AppRadioGroup/AppRadioGroup';
import Grid from 'components/core/Grid';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import { CheckoutSectionTitle } from 'components/views/pages/Checkout/components/CheckoutSectionTitle';
import { BookingCheckoutResponse } from 'components/views/pages/Checkout/hooks/useCheckout';
import { CheckoutParamEnum, PaymentTypeEnum } from 'components/views/pages/Checkout/types';
import dayjs from 'dayjs';
import { formatCanadianCurrency } from 'helpers/currencyHelpers';
import { useAppParam } from 'hooks/useAppParam';
import styled from 'styled-components';
import { usePrevious } from 'hooks/usePrevious';
import { useEffect, useMemo } from 'react';
import { CheckoutBoxWrapper } from '../../styles';

export const SecureGrid = styled(Grid)`
  display: flex;
  align-items: center;

  & svg {
    margin-right: ${props => props.theme.spacing(1)};
    color: ${props => props.theme.palette.primary.main};
  }
`;

const RightComponent = ({ amount }: { amount: string }) => {
  return (
    <Hidden smDown>
      <Typography fontWeight={700} alignSelf="center" mx={2}>
        {amount}
      </Typography>
    </Hidden>
  );
};

interface Props {
  checkoutData: BookingCheckoutResponse | undefined;
  paymentType: string | undefined;
  arrivalDate: string | undefined;
  currency_code: string;
  currency_multiplier: number;
}

export const PaymentSchedule = ({ checkoutData, paymentType, arrivalDate, currency_code, currency_multiplier }: Props) => {
  const { setParam } = useAppParam(CheckoutParamEnum.paymentType);

  const fullAmount = formatCanadianCurrency(checkoutData?.BOOKING?.TOTAL ?? 0, { minMaxFractionDigits: 2, currency_code, currency_multiplier });
  const depositDue = formatCanadianCurrency(checkoutData?.BOOKING?.PAYMENT?.DEPOSIT?.TOTAL ?? 0, {
    minMaxFractionDigits: 2, currency_code, currency_multiplier
  });
  const balanceDue = formatCanadianCurrency(checkoutData?.BOOKING?.PAYMENT?.BALANCE?.TOTAL ?? 0, {
    minMaxFractionDigits: 2, currency_code, currency_multiplier
  });
  const balanceDueDate = checkoutData ? dateFormatLongMonth(dayjs(checkoutData.BOOKING.PAYMENT.BALANCE.DUE_DATE)) : '';
  const isGatewayClosed = checkoutData
    ? checkoutData.BOOKING.PROPERTY.GATEWAY_MODE.ID === BookingGateWayModeIdEnum.closed
    : false;

  const buttons = useMemo(() => {
    const buttons: AppRadioGroupProps['buttons'] = [];
    const button1 = {
      title: 'Full Payment',
      subtitle: `${fullAmount} ${isGatewayClosed ? 'due on booking request approval' : 'today.'}`,
      value: PaymentTypeEnum.PayInFull.toString(),
      rightComponent: <RightComponent amount={fullAmount} />,
    };

    const button2 = {
      title: 'Payment Schedule',
      subtitle: `${depositDue} today and ${balanceDue} by ${balanceDueDate}.`,
      value: PaymentTypeEnum.DepositBalanceDue.toString(),
      rightComponent: <RightComponent amount={depositDue} />,
    };


    const today = dayjs();
    const arrival = arrivalDate ? dayjs(arrivalDate) : dayjs();

    if (Math.abs(today.diff(arrival, 'day')) >= 45) {
      buttons.push(button2);
      buttons.push(button1);
    } else {
      buttons.push(button1);
    }

    return buttons;
  }, [arrivalDate, balanceDue, balanceDueDate, depositDue, fullAmount, isGatewayClosed]);

  // Set payment type to pay in full if button options ever reduce to 1
  const prevButtons = usePrevious(buttons);

  useEffect(() => {
    if (prevButtons && prevButtons.length > 1 && buttons.length === 1) {
      setParam(PaymentTypeEnum.PayInFull);
    }
  }, [buttons.length, prevButtons, setParam]);

  const isLoading = !checkoutData || !paymentType || !arrivalDate;

  const setValue = (val: any) => {
    setParam(val);
  };

  return (
    <>
      <CheckoutBoxWrapper>
        <Grid item xs={12}>

          <LoadingSkeleton isLoading={isLoading}>
            <CheckoutSectionTitle>Payment Options</CheckoutSectionTitle>
          </LoadingSkeleton>
          <LoadingSkeleton isLoading={isLoading} scaleOneToOne>
            <AppRadioGroup
              buttons={buttons}
              value={paymentType ?? ''}
              setValue={setValue}
              titleStyle={{ fontSize: '16px' }}
              color="inherit"
              disabled={isLoading}
              isSingleColumn
            />
          </LoadingSkeleton>
          <AppDivider borderWidth='0' />
          <Hidden mdUp>
            <SecureGrid item xs={12} mt={2} mb={2}>
              <LockIcon />
              <Typography variant="body1">Payment is 100% secure using SSL encryption</Typography>
            </SecureGrid>
            <AppDivider marginVertical="4px" borderWidth='0' />
          </Hidden>

          <Typography variant="body1">* You will not be charged until the last step in the confirmation process.</Typography>
        </Grid>
      </CheckoutBoxWrapper>
    </>
  );
};
